import { defineStore, getActivePinia, acceptHMRUpdate } from 'pinia';
import {
    ITEM_LOCATION_TYPES,
    ROADMAP_REQUEST_FIELDS,
    ROADMAP_TYPES,
    SPECIAL_ACTION_IDS,
} from '@/roadmaps/constants/roadmaps.constants';
import { useDraggable } from '@/roadmaps/use/use-draggable';
import { useVocItemsStore } from '@/stores/voc-items.store';

import * as api from '@/stores/api/roadmaps.api';
import { asyncQueue } from '@/stores/helpers/roadmaps-async-queue.helper';
import each from 'lodash/each';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import reject from 'lodash/reject';
import intersectionWith from 'lodash/intersectionWith';

import { unref } from 'vue';

interface RoadmapItem {
    id: string;
    type?: string;
    parentType?: string;
    parentId?: string;
    columnId?: string;
    row?: number;
    onBacklog?: boolean;
    isExpanded?: boolean;
    importedItemId?: string;
    roadmapId?: string;
    error?: unknown;
    [key: string]: unknown;
}

interface ItemsState {
    items: Record<string, RoadmapItem>;
    itemPromises: Partial<Record<string, Promise<unknown>>>;
}

export function getInitialState (): ItemsState {
    return {
        items: {},
        itemPromises: {},
    };
}

export const useRoadmapsItemsStore = defineStore('roadmaps/items', {
    state: getInitialState,
    getters: {
        items (): Record<string, RoadmapItem> {
            const rawState = getActivePinia()!.state.value['roadmaps/items'] as ItemsState;
            const itemsWithQueuedUpdates = {
                ...rawState.items,
                ...(asyncQueue.queuedUpdates.items as Record<string, RoadmapItem>),
            };

            return mapValues(itemsWithQueuedUpdates, (item) => {
                if (item.type !== ITEM_LOCATION_TYPES.IDEA.key) return item;

                if (!item.importedItemId) return item;
                const vocItem = useVocItemsStore().vocItemById(item.importedItemId);
                if (!vocItem) return item;

                return {
                    ...item,
                    title: vocItem.formData[0].content,
                };
            });
        },
        itemById () {
            return (itemId: string): RoadmapItem | undefined => this.items[itemId];
        },
        itemsByParentId () {
            return (parentId: string): RoadmapItem[] =>
                filter(this.items, (item) => item.parentId === parentId);
        },
        swimlaneItemsByRow () {
            return (swimlaneId: string, row: number): RoadmapItem[] =>
                filter(this.items, (item) => item.row === row && item.parentId === swimlaneId);
        },
        swimlaneItemsBelowRow () {
            return (swimlaneId: string, row: number): RoadmapItem[] =>
                filter(this.items, (item) => (item.row ?? 0) > row && item.parentId === swimlaneId);
        },
        childItemsWithoutDraggedItem () {
            return (parentId: string): RoadmapItem[] => {
                const childrenByParent = this.itemsByParentId(parentId);
                const dragged = unref(useDraggable().draggedItem) as { id: string } | null;
                if (!dragged) return childrenByParent;

                return reject(childrenByParent, (child) => dragged.id === child.id);
            };
        },
        backlogItems (): RoadmapItem[] {
            return filter(this.items, (item) => !!item.onBacklog) as unknown as RoadmapItem[];
        },
        itemsByColumnId () {
            return (columnId: string): RoadmapItem[] =>
                filter(this.items, (item) => item.columnId === columnId);
        },
        itemsByParentIdAndColumnId () {
            return (parentId: string, columnId: string): RoadmapItem[] =>
                filter(this.items, (item) => item.columnId === columnId && item.parentId === parentId);
        },
        itemsByParentIdAndColumnIdWithoutDraggedItem () {
            return (parentId: string, columnId: string): RoadmapItem[] => {
                const itemsByParentAndColumnId = this.itemsByParentIdAndColumnId(parentId, columnId);
                const dragged = unref(useDraggable().draggedItem) as { id: string } | null;
                if (!dragged) return itemsByParentAndColumnId;

                return reject(itemsByParentAndColumnId, (item) => dragged.id === item.id);
            };
        },
        itemsByType () {
            return (type: string): RoadmapItem[] =>
                filter(this.items, (item) => item.type === type);
        },
    },
    actions: {
        deleteByKey (itemId: string) {
            this.$patch((state) => {
                delete state.items[itemId];
            });
        },
        setMap ({ items }: { items: Record<string, unknown> }) {
            this.$patch((state) => {
                state.items = mapValues(items, (item) => Object.freeze(item)) as Record<string, RoadmapItem>;
            });
        },
        updateMap ({ items }: { items: Record<string, Partial<RoadmapItem>> }) {
            this.$patch((state) => {
                const itemsToUpdate = mapValues(items, (item) => {
                    return Object.assign({}, state.items[(item as RoadmapItem).id ?? ''], item);
                });
                const itemsUpdated = Object.assign({}, state.items, itemsToUpdate);
                state.items = mapValues(itemsUpdated, (item) => Object.freeze(item)) as Record<string, RoadmapItem>;
            });
        },
        updateMapAtId ({ id, item }: { id: string; item: Partial<RoadmapItem> }) {
            this.$patch((state) => {
                state.items[id] = Object.freeze(Object.assign({}, state.items[id], item)) as RoadmapItem;
            });
        },
        updateMapAtKey ({ id, key, value }: { id: string; key: string; value: unknown }) {
            this.$patch((state) => {
                state.items[id] = Object.freeze(Object.assign({}, state.items[id], { [key]: value })) as RoadmapItem;
            });
        },
        updateItemPromises ({ itemId, promise }: { itemId: string; promise: Promise<unknown> | null }) {
            if (promise) {
                this.itemPromises[itemId] = promise;
            } else if (this.itemPromises[itemId]) {
                delete this.itemPromises[itemId];
            }
        },
        async loadOne ({ roadmapId, itemId }: { roadmapId: string; itemId: string }) {
            if (this.itemPromises[itemId]) {
                return this.itemPromises[itemId];
            }

            try {
                const promise = api.fetchRoadmapItem(roadmapId, itemId);
                this.updateItemPromises({ itemId, promise });
                const item = await promise;
                this.updateMapAtId({ id: itemId, item: { ...item, roadmapId } });
            } catch (error) {
                this.updateMapAtId({ id: itemId, item: { error } });
                throw error;
            } finally {
                this.updateItemPromises({ itemId, promise: null });
            }
        },
        loadMany ({ items }: { items: Array<{ roadmapId: string; id: string }> }): Promise<unknown[]> {
            const promises = map(items, (item) => {
                return this.loadOne({ roadmapId: item.roadmapId, itemId: item.id });
            });

            return Promise.allSettled(promises);
        },
        async fetchRoadmapBacklog (): Promise<Record<string, RoadmapItem>> {
            const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
            const { currentRoadmap } = useRoadmapsStore();
            if (!currentRoadmap) return {};
            const backlogItems = await api.fetchRoadmapBacklog(currentRoadmap.id);
            this.updateMap({ items: backlogItems });

            return backlogItems;
        },
        async createItem ({
            item,
            resolvingItems = [],
        }: {
            item: Partial<RoadmapItem>;
            resolvingItems?: Partial<RoadmapItem>[];
        }): Promise<Record<string, RoadmapItem>> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const payload = {
                    item: pick(
                        item,
                        currentRoadmap!.type === ROADMAP_TYPES.TIMELINE
                            ? ROADMAP_REQUEST_FIELDS.timelineItemCreate
                            : ROADMAP_REQUEST_FIELDS.columnItemCreate,
                    ),
                    resolveItemLocations: map(resolvingItems, (item) => {
                        return pick(
                            item,
                            currentRoadmap!.type === ROADMAP_TYPES.TIMELINE
                                ? ROADMAP_REQUEST_FIELDS.timelineItemLocationsUpdate
                                : ROADMAP_REQUEST_FIELDS.columnItemLocationsUpdate,
                        );
                    }),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };

                const responseItems = await api.createRoadmapItem(currentRoadmap!.id, payload);
                this.updateMap({ items: responseItems });
                const { setPendingActionNone } = await import('@/roadmaps/use/use-pending-actions');
                each(responseItems, (item) => {
                    setPendingActionNone(item.id);
                });

                return responseItems;
            };

            return asyncQueue.enqueue({
                task,
                updates: {
                    [SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM]: { ...item, id: SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM },
                    ...keyBy(resolvingItems, 'id'),
                },
                updateType: 'items',
            });
        },
        async updateItem (itemToUpdate: RoadmapItem) {
            const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
            const { currentRoadmap } = useRoadmapsStore();
            if (!currentRoadmap) return;
            const payload = {
                item: pick(itemToUpdate, ROADMAP_REQUEST_FIELDS.item),
            };
            const item = await api.updateRoadmapItem(currentRoadmap.id, payload);
            this.updateMapAtId({ id: itemToUpdate.id, item });
        },
        async updateLocations (itemsToUpdate: RoadmapItem[]): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const roadmapsStore = useRoadmapsStore();
                const { currentRoadmap } = roadmapsStore;
                const payload = {
                    itemLocations: map(itemsToUpdate, (item) => {
                        return pick(
                            item,
                            currentRoadmap!.type === ROADMAP_TYPES.TIMELINE
                                ? ROADMAP_REQUEST_FIELDS.timelineItemLocationsUpdate
                                : ROADMAP_REQUEST_FIELDS.columnItemLocationsUpdate,
                        );
                    }),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const updatedRoadmap = await api.updateItemLocations(currentRoadmap!.id, payload);
                await roadmapsStore.setOne(updatedRoadmap);

                return updatedRoadmap;
            };

            return asyncQueue.enqueue({
                task,
                updates: keyBy(itemsToUpdate, 'id'),
                updateType: 'items',
            });
        },
        async promoteItems ({
            parent,
            children,
            importedItem,
            resolvingItems,
        }: {
            parent: Partial<RoadmapItem>;
            children: Partial<RoadmapItem>[];
            importedItem?: Partial<RoadmapItem>;
            resolvingItems: Partial<RoadmapItem>[];
        }): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const payload: Record<string, unknown> = {
                    parentItem: pick(parent, ROADMAP_REQUEST_FIELDS.item),
                    childItemLocations: children.map((child) => pick(child, ROADMAP_REQUEST_FIELDS.item)),
                    resolveItemLocations: resolvingItems.map((item) => pick(item, ROADMAP_REQUEST_FIELDS.item)),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };

                if (importedItem) payload.importedItem = pick(importedItem, ROADMAP_REQUEST_FIELDS.item);

                const resItems = await api.promoteItems(currentRoadmap!.id, payload);
                this.updateMap({ items: resItems });

                return resItems;
            };

            const childrenWithPromoteParentId = map(children, (child) => ({
                ...child,
                parentId: SPECIAL_ACTION_IDS.PROMOTE_PARENT,
            }));

            const asyncQueueUpdates: Record<string, unknown> = {
                ...keyBy(childrenWithPromoteParentId, 'id'),
                ...keyBy(resolvingItems, 'id'),
                [SPECIAL_ACTION_IDS.PROMOTE_PARENT]: { ...parent, id: SPECIAL_ACTION_IDS.PROMOTE_PARENT },
            };
            if (importedItem) asyncQueueUpdates[(importedItem as RoadmapItem).id] = importedItem;

            return asyncQueue.enqueue({
                task,
                updates: asyncQueueUpdates,
                updateType: 'items',
            });
        },
        async deleteItem (itemId: string): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const roadmapsStore = useRoadmapsStore();
                const { currentRoadmap } = roadmapsStore;
                const payload = {
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };
                const updatedRoadmap = await api.deleteRoadmapItem(currentRoadmap!.id, itemId, payload);
                await roadmapsStore.setOne(updatedRoadmap);

                return updatedRoadmap;
            };

            return asyncQueue.enqueue({
                task,
                updates: {},
                updateType: 'items',
            });
        },
        async createIdeaOnRoadmap ({
            ideaToCreateOnRoadmap,
            resolvingItems,
        }: {
            ideaToCreateOnRoadmap: Partial<RoadmapItem>;
            resolvingItems: Partial<RoadmapItem>[];
        }): Promise<unknown> {
            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const payload = {
                    item: pick(
                        ideaToCreateOnRoadmap,
                        currentRoadmap!.type === ROADMAP_TYPES.TIMELINE
                            ? ROADMAP_REQUEST_FIELDS.timelineItemCreate
                            : ROADMAP_REQUEST_FIELDS.columnItemCreate,
                    ),
                    resolveItemLocations: resolvingItems.map((item) => pick(item, ROADMAP_REQUEST_FIELDS.item)),
                    roadmapVersionTimestamp: currentRoadmap?.lastUpdatedAt,
                };

                const responseItems = await api.createIdeaItemOnRoadmap(
                    currentRoadmap!.id,
                    ideaToCreateOnRoadmap.importedItemId as string,
                    payload,
                );
                this.updateMap({ items: responseItems });
                const { setPendingActionNone } = await import('@/roadmaps/use/use-pending-actions');
                each(responseItems, (item) => {
                    setPendingActionNone(item.id);
                });

                return responseItems;
            };

            return asyncQueue.enqueue({
                task,
                updates: {
                    [SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM]: {
                        ...ideaToCreateOnRoadmap,
                        id: SPECIAL_ACTION_IDS.PLACEHOLDER_ITEM,
                    },
                    ...keyBy(resolvingItems, 'id'),
                },
                updateType: 'items',
            });
        },
        async promoteIdeaToFeature ({ itemId }: { itemId: string }): Promise<unknown> {
            const roadmapItem = this.itemById(itemId);

            const task = async () => {
                const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
                const { currentRoadmap } = useRoadmapsStore();
                const responseItem = await api.promoteIdeaToFeature(currentRoadmap!.id, itemId);
                this.updateMapAtId({ id: itemId, item: responseItem });

                return responseItem;
            };

            return asyncQueue.enqueue({
                task,
                updates: keyBy([roadmapItem].filter(Boolean), 'id'),
                updateType: 'items',
            });
        },
        async fetchRoadmapItemExternalIdeas ({ itemId }: { itemId: string }): Promise<unknown[]> {
            const { useRoadmapsStore } = await import('@/stores/roadmaps.store');
            const { currentRoadmap } = useRoadmapsStore();
            const vocItems = useVocItemsStore().list;

            const externalIdeas = await api.getRoadmapItemExternalIdeas(currentRoadmap!.id, itemId);

            return intersectionWith(
                externalIdeas as { externalId: string }[],
                vocItems as { id: string }[],
                (externalIdea, vocItem) => externalIdea.externalId === (vocItem as { id: string }).id,
            );
        },
        setIsExpanded ({ id, expanded }: { id: string; expanded: boolean }) {
            this.updateMapAtKey({ id, key: 'isExpanded', value: expanded });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useRoadmapsItemsStore, module.hot));
}
